/**
 * Import Template — the admin doorway for templates authored outside the editor.
 *
 * Upload, drag-and-drop and paste are three affordances over one code path:
 * every one of them ends in the same TemplateImporter.import call, so a parser
 * can never tell how a template arrived and the three can never drift.
 *
 * Nothing here renders a template by hand. The preview is PageThumbnailPreview,
 * the same component the editor and the export pipeline use, so what an
 * operator approves is what the product will produce.
 *
 * Nothing reaches the database until Import is pressed. A malformed template
 * can be pasted, looked at, corrected and looked at again without leaving a
 * record behind.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Upload, ClipboardCopy, Loader2, AlertTriangle, AlertCircle, Info,
  CheckCircle2, ChevronLeft, ChevronRight, FileJson,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import PageThumbnailPreview from '../../components/Editor/PageThumbnailPreview';
import { useToast } from '../../hooks/use-toast';
import { TemplateImporter } from '../../templateEngine';
import { schemaForClipboard } from './importSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Long enough that typing does not thrash the parser, short enough to feel live. */
const REPARSE_DEBOUNCE_MS = 300;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// ─── Findings list ────────────────────────────────────────────────────────────

const FINDING_STYLES = {
  error: { icon: AlertCircle, className: 'text-red-600', label: 'Error' },
  warning: { icon: AlertTriangle, className: 'text-amber-600', label: 'Warning' },
  info: { icon: Info, className: 'text-blue-600', label: 'Note' },
};

const FindingList = ({ kind, items }) => {
  if (!items.length) return null;
  const { icon: Icon, className, label } = FINDING_STYLES[kind];

  return (
    <ul className="space-y-1" data-testid={`findings-${kind}`}>
      {items.map((item, i) => (
        <li key={`${kind}-${i}`} className={`flex items-start gap-2 text-sm ${className}`}>
          <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          <span className="sr-only">{label}: </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

const AdminTemplateImport = () => {
  const { toast } = useToast();

  const [rawText, setRawText] = useState('');
  const [result, setResult] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [tags, setTags] = useState('');
  const [isPremium, setIsPremium] = useState(false);

  const [duplicate, setDuplicate] = useState(null);
  const [replacing, setReplacing] = useState(null);
  const [importing, setImporting] = useState(false);

  const fileInputRef = useRef(null);
  const schemaRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/content/categories?type=template`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);

  // ── Parse, debounced ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!rawText.trim()) {
      setResult(null);
      setDuplicate(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      // Abandon the previous run before starting another: on a large document
      // the importer would otherwise still be working on text the operator has
      // already replaced.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setParsing(true);
      try {
        const parsed = await TemplateImporter.import(rawText, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setResult(parsed);
        setPageIndex(0);
      } catch (err) {
        // The importer contracts never to throw. If it ever does, the operator
        // gets a message rather than a spinner that never stops — a stuck
        // spinner leaves Import permanently disabled with nothing on screen to
        // explain why, and only a reload recovers it.
        toast({ title: 'Could not read this template', description: err.message, variant: 'destructive' });
      } finally {
        if (!controller.signal.aborted) setParsing(false);
      }
    }, REPARSE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [rawText]);

  // ── Duplicate lookup ────────────────────────────────────────────────────────
  useEffect(() => {
    const hash = result?.metadata?.content_hash;
    if (!hash) {
      setDuplicate(null);
      return;
    }

    let cancelled = false;
    fetch(`${API_URL}/api/admin/content/templates/by-content-hash/${hash}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setDuplicate(body?.exists ? body.template : null); })
      // A lookup failure loses duplicate detection, nothing more. It must never
      // stand between an operator and an import that is otherwise valid.
      .catch(() => { if (!cancelled) setDuplicate(null); });

    return () => { cancelled = true; };
  }, [result]);

  // ── What an explicit library_id would replace ───────────────────────────────
  // Only when the id came from the document. A derived id can collide too, but
  // that is the duplicate case and is already reported by content hash; this
  // warning is for the operator who typed an id and may not realise it points
  // at something already published.
  useEffect(() => {
    const meta = result?.metadata;
    if (!meta?.library_id_explicit) {
      setReplacing(null);
      return;
    }

    let cancelled = false;
    fetch(`${API_URL}/api/admin/content/templates/by-library-id/${encodeURIComponent(meta.library_id)}`,
      { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setReplacing(body?.exists ? body.template : null); })
      .catch(() => { if (!cancelled) setReplacing(null); });

    return () => { cancelled = true; };
  }, [result]);

  /**
   * Read a dropped or picked file as text.
   *
   * `Blob.prototype.text` is missing from older Safari and from jsdom, so the
   * FileReader fallback is load-bearing rather than defensive — calling
   * `file.text()` directly threw here. The engine's normalizeInput solves the
   * same problem, but it is not on the frozen public surface, and this screen
   * needs the text itself to put in the editor, not just a parse result.
   */
  const readFileText = (file) => {
    if (typeof file.text === 'function') return file.text();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error || new Error('Could not read file'));
      reader.readAsText(file);
    });
  };

  const loadFile = useCallback(async (file) => {
    if (!file) return;
    try {
      setRawText(await readFileText(file));
    } catch (err) {
      toast({ title: 'Could not read file', description: err.message, variant: 'destructive' });
    }
  }, [toast]);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    setDragging(false);
    loadFile(event.dataTransfer?.files?.[0]);
  }, [loadFile]);

  const copySchema = useCallback(async () => {
    const text = schemaForClipboard();
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Schema copied', description: 'Paste it into your model of choice.' });
    } catch {
      // No clipboard permission, or an insecure context. Show the text and
      // select it so the operator can copy it themselves.
      if (schemaRef.current) {
        schemaRef.current.value = text;
        schemaRef.current.select();
      }
      toast({
        title: 'Could not copy automatically',
        description: 'The schema is selected below — copy it manually.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const errors = result?.report?.errors ?? [];
  const warnings = result?.report?.warnings ?? [];
  const notes = result?.report?.partial ?? [];
  const canImport = Boolean(result) && errors.length === 0 && !parsing && !importing;

  const previewPages = result?.preview?.pages ?? [];
  const currentPage = result?.document?.pages?.[pageIndex] ?? null;
  const canvasSize = useMemo(() => ({
    width: result?.preview?.canvas?.width || 1080,
    height: result?.preview?.canvas?.height || 1080,
  }), [result]);

  const runImport = useCallback(async () => {
    if (!result) return;
    setImporting(true);

    const doc = result.document;
    const extraTags = tags.split(',').map((t) => t.trim()).filter(Boolean);

    const payload = {
      library_id: result.metadata.library_id,
      name: doc.name || 'Untitled',
      description: doc.description || null,
      canvas_width: doc.canvas_width,
      canvas_height: doc.canvas_height,
      canvas_background: doc.canvas_background,
      template_json: { pages: doc.pages },
      tags: [...(doc.tags || []), ...extraTags],
      is_premium: isPremium || doc.is_premium,
      source: 'imported',
      status: 'pending',
      parser: result.metadata.parser,
      parser_version: result.metadata.parser_version,
      ...(result.metadata.content_hash ? { content_hash: result.metadata.content_hash } : {}),
      ...(categoryId ? { category_id: categoryId } : {}),
    };

    try {
      const res = await fetch(`${API_URL}/api/admin/content/templates/import-library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ templates: [payload] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const body = await res.json();
      const outcome = body.results?.[0];
      if (!outcome?.ok) throw new Error(outcome?.error || 'Import rejected');

      toast({
        title: outcome.action === 'updated' ? 'Template updated' : 'Template imported',
        description: 'Review and publish it under Moderation → Template Review.',
      });
      setRawText('');
      setResult(null);
    } catch (err) {
      toast({ title: 'Import failed', description: err.message, variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  }, [result, tags, isPremium, categoryId, toast]);

  return (
    <AdminLayout>
      <div className="p-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
              <FileJson className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Import Template</h1>
              <p className="text-sm text-gray-500">
                Paste or upload template JSON. Nothing is saved until you import.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              data-testid="file-input"
              onChange={(e) => loadFile(e.target.files?.[0])}
            />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-1.5" /> Upload .json
            </Button>
            <Button variant="outline" size="sm" onClick={copySchema}>
              <ClipboardCopy className="w-4 h-4 mr-1.5" /> Copy JSON Schema
            </Button>
          </div>
        </div>

        {/* Editor + preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            data-testid="drop-zone"
            className={`rounded-xl border-2 border-dashed transition-colors ${
              dragging ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white'
            }`}
          >
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              spellCheck={false}
              aria-label="Template JSON"
              placeholder={'Paste template JSON here, drop a .json file, or use Upload.'}
              className="w-full h-[460px] p-4 font-mono text-xs bg-transparent resize-none outline-none"
            />
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex flex-col">
            <div className="flex-1 flex items-center justify-center overflow-hidden">
              {parsing && <Loader2 className="w-6 h-6 animate-spin text-purple-500" />}
              {!parsing && !currentPage && (
                <p className="text-sm text-gray-400">The preview appears once the JSON parses.</p>
              )}
              {!parsing && currentPage && (
                <div className="w-full max-w-[420px]" data-testid="preview">
                  <PageThumbnailPreview page={currentPage} canvasSize={canvasSize} fillWidth />
                </div>
              )}
            </div>

            {previewPages.length > 0 && (
              <div className="flex items-center justify-center gap-3 pt-3 text-sm text-gray-600">
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={pageIndex === 0}
                  onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span data-testid="page-indicator">
                  Page {pageIndex + 1} / {previewPages.length}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={pageIndex >= previewPages.length - 1}
                  onClick={() => setPageIndex((i) => Math.min(previewPages.length - 1, i + 1))}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Findings */}
        {result && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4 space-y-2">
            {errors.length === 0 && (
              <p className="flex items-center gap-2 text-sm text-emerald-600">
                <CheckCircle2 className="w-4 h-4" /> This template is ready to import.
              </p>
            )}
            <FindingList kind="error" items={errors} />
            <FindingList kind="warning" items={warnings} />
            <FindingList kind="info" items={notes} />

            {duplicate && (
              <p
                className="flex items-start gap-2 text-sm text-amber-600"
                data-testid="duplicate-warning"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  An identical template already exists: <strong>{duplicate.name}</strong>{' '}
                  ({duplicate.status}). Importing will update it rather than create a copy.
                </span>
              </p>
            )}

            {replacing && (
              <p
                className="flex items-start gap-2 text-sm text-amber-600"
                data-testid="replace-warning"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  This JSON declares <code>library_id</code>{' '}
                  <strong>{result.metadata.library_id}</strong>, which already belongs to{' '}
                  <strong>{replacing.name}</strong>{' '}
                  ({replacing.is_active === false ? 'deleted' : replacing.status}). Importing
                  will overwrite that template
                  {replacing.is_active === false && ', and restore it'}.
                </span>
              </p>
            )}

            {result.variables.length > 0 && (
              <p className="text-sm text-gray-500">
                {result.variables.length} placeholder
                {result.variables.length === 1 ? '' : 's'} detected:{' '}
                {result.variables.map((v) => v.token).join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Publish bar */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-wrap items-end gap-4">
          <div className="min-w-[200px]">
            <Label className="text-xs text-gray-500">Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="h-9 mt-1">
                <SelectValue placeholder="Uncategorized" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[240px] flex-1">
            <Label className="text-xs text-gray-500" htmlFor="import-tags">
              Extra tags (comma separated)
            </Label>
            <Input
              id="import-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="flyer, real estate"
              className="h-9 mt-1"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 h-9">
            <input
              type="checkbox"
              checked={isPremium}
              onChange={(e) => setIsPremium(e.target.checked)}
              className="rounded border-gray-300"
            />
            Premium
          </label>

          <Button
            className="bg-purple-600 hover:bg-purple-700 ml-auto"
            disabled={!canImport}
            onClick={runImport}
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import as Pending
          </Button>
        </div>

        {/* Clipboard fallback target. Off-screen until Copy Schema needs it. */}
        <textarea
          ref={schemaRef}
          readOnly
          aria-hidden
          tabIndex={-1}
          className="absolute left-[-9999px] w-px h-px"
          data-testid="schema-fallback"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminTemplateImport;
